declare global {
  interface Window {
    oaiq?: (...args: unknown[]) => void;
  }
}

/**
 * Sinal de conversão para o OpenAI Ads.
 *
 * Até 20/10/2026 isto só era chamado por trackFormSubmission. Resultado medido
 * entre 01 e 19/10: a campanha nj_familia_set26 gastou $163,21 em 36 cliques e
 * o painel reportava 0 conversões — mas o GA4 registrava 2 whatsapp_click com
 * origem chatgpt.com. Eram duas conversões reais que o algoritmo nunca viu, e
 * ele passou quatro dias otimizando contra um zero falso.
 *
 * Agora telefone e WhatsApp também disparam. Nesse público — imigrante, no
 * celular, 84% dentro do app do ChatGPT — tocar no WhatsApp é a conversão
 * principal, não o formulário: no mesmo período o formulário teve ZERO
 * form_start vindos do ChatGPT.
 *
 * DEDUPE POR SESSÃO, e vale saber por quê. Sem isso, quem toca no botão três
 * vezes vira três leads. Uma pessoa com um problema é um lead, então só o
 * primeiro disparo da sessão conta — inclusive entre tipos diferentes, para que
 * tocar no WhatsApp e depois enviar o formulário não conte duas vezes.
 *
 * O efeito colateral: se a pessoa tocar no WhatsApp e DEPOIS enviar o
 * formulário, o envio não dispara, mesmo sendo o sinal mais forte dos dois. É
 * uma troca consciente — inflar a contagem seria pior que perder a distinção,
 * e o OpenAI Ads não recebe o tipo de ação de qualquer forma. Para contar um
 * por tipo em vez de um por sessão, basta trocar a chave por `${KEY}_${source}`.
 *
 * sessionStorage pode lançar em janela privada e em webview de app, que é
 * justamente de onde vem a maior parte desse tráfego — por isso o fallback em
 * memória, que ao menos cobre a sessão de página única.
 */

const KEY = "arandia_oaiq_lead";

let reportedInMemory = false;

function alreadyReported(): boolean {
  if (reportedInMemory) return true;
  try {
    return window.sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

function markReported(): void {
  reportedInMemory = true;
  try {
    window.sessionStorage.setItem(KEY, "1");
  } catch {
    // webview sem storage: o fallback em memória segura a sessão atual
  }
}

/**
 * @param source apenas para depuração e para o teste. NÃO vai no payload: o
 * objeto enviado é exatamente o que já funcionava antes, e acrescentar campo
 * não documentado a um pixel que estamos consertando é o tipo de risco que não
 * compensa.
 */
export function trackOpenAILead(source?: "form" | "whatsapp" | "phone"): void {
  try {
    if (typeof window === "undefined" || typeof window.oaiq !== "function") return;
    if (alreadyReported()) return;
    window.oaiq("measure", "lead_created", { type: "customer_action" });
    markReported();
  } catch {
    // nunca deixar medição quebrar um envio de formulário ou um clique
  }
  void source;
}

/** Só para teste: zera o dedupe entre casos. */
export function __resetOpenAILeadDedupe(): void {
  reportedInMemory = false;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}

export {};
